import { useCallback, useMemo } from "react";

import { TOUR_STORAGE_PREFIX } from "./constants";
import {
  applyTourEvent,
  DEFAULT_TOUR_STATE,
  deriveTourFlags,
  parseTourState,
  type TourEventBody,
  type TourState,
} from "./tourState";
import type { TourDefinition } from "./types";

import { useAuth } from "@/lib/hooks/useAuth";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";

export function useTourState({ id, steps }: Pick<TourDefinition, "id" | "steps">) {
  const { userId } = useAuth();
  const stepCount = steps.length;
  const storageOptions = useMemo(
    () => ({ deserializer: (raw: string) => parseTourState(JSON.parse(raw), stepCount) }),
    [stepCount],
  );
  const [stored, setStored] = useLocalStorage<TourState>(
    `${TOUR_STORAGE_PREFIX}${id}:state:${userId ?? ""}`,
    DEFAULT_TOUR_STATE,
    storageOptions,
  );

  const record = useCallback(
    (body: TourEventBody) => {
      if (!userId) return;
      setStored((current) => applyTourEvent(current, body, stepCount));
    },
    [userId, setStored, stepCount],
  );

  return { ...deriveTourFlags(userId ? stored : DEFAULT_TOUR_STATE), record };
}
